import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import QRCode from 'react-qr-code'
import { useFirestore, useFirestoreGeneral } from '../../../firebase/useFirestore'
import useHouseholdParticipations from '../../../hooks/useHouseholdParticipations'
import hasProgrammeQuestionnaire from '../../../helpers/households/hasProgrammeQuestionnaire'
import { client } from '../../../hooks/Client'
import useIsMobileUserAgent from '../../../hooks/useIsMobileUserAgent'
import buildQuestionnaireShareLinks from '../../../helpers/questionnaires/buildQuestionnaireShareLinks'
import { getQrcodeValue } from '../../../pages/measurementPlan/qrcodeHelpers'
import CopyLink from '../../common/CopyLink'
import Tooltip from '../../common/Tooltip'
import QrcodeIcon from '../../../images/icons/qrcode-icon.png'
import languages from '../../../utils/listOfLanguages'
import ProgrammeResearchLink from './ProgrammeResearchLink'
import { effectiveResearchDocs } from '../../../helpers/measurementSignals/effectiveResearchIds'

/**
 * De onderzoeken die aan een huishouden gekoppeld zijn, met per onderzoek een
 * deelbare link en een QR-code. Meetmoment en taal kies je hier, want die
 * bepalen allebei de link.
 *
 * @param {object} props
 * @param {string} props.adressID - Adresses.ID van het huishouden (niet de docid)
 * @param {'page'|'inline'} [props.qrMode] - Waar de QR-code terechtkomt.
 *   `'page'` opent de printbare QR-pagina; dat is de bureaubladweg. `'inline'`
 *   tekent de code hier op het scherm — de enige werkende vorm op de telefoon,
 *   want daar is geen printer én bestaat de QR-route niet in de mobiele
 *   routering, dus een klik zou op een leeg scherm eindigen.
 *   Laat je hem weg, dan beslist het schermtype. Dat is met opzet geen keuze van
 *   de aanroeper: deze component hangt óók in de afspraakmodal, en die is via de
 *   gedeelde agenda net zo goed op een telefoon te openen.
 *   AI-CONTEXT: See docs/pitfalls.md#ui-shared-affordance-gated-per-caller
 */
const HouseholdResearchLinks = ({ adressID, qrMode }) => {
    // Hooks
    const history = useHistory()
    const isMobile = useIsMobileUserAgent()
    const effectiveQrMode = qrMode || (isMobile ? 'inline' : 'page')

    // State — per onderzoek een eigen keuze; de sleutel is Research.ID.
    const [momentByResearch, setMomentByResearch] = useState({})
    const [languageByResearch, setLanguageByResearch] = useState({})

    // Firestore
    const adress = useFirestoreGeneral('Adresses', 'ID', adressID || '')
    const researchDocs = useFirestore('Research')
    const measureMoments = useFirestore('MeasureMoments')

    const participations = useHouseholdParticipations(adress?.[0]?.HouseholdKey || '')

    const adressDoc = adress?.[0]

    // Wie doet mee: handmatig gekoppeld (in de volgorde op het huishouden) plus
    // elk onderzoek dat via de schakelaar voor alle huishoudens geldt.
    // AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md
    const linkedResearch = useMemo(
        () => effectiveResearchDocs({ kind: 'adress', linkedIds: adressDoc?.Research, researches: researchDocs }),
        [adressDoc, researchDocs]
    )
    // Zolang de onderzoeken onderweg zijn mag dit scherm niet beweren dat er
    // niets gekoppeld is: een onderzoek voor alle huishoudens kan er nog aankomen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const researchLoaded = Array.isArray(researchDocs)

    const momentsByResearch = useMemo(() => {
        const map = new Map()
        // Client-side sorteren: `Position` ontbreekt op oudere meetmomenten en
        // een orderBy zou die stil weglaten.
        // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
        const sorted = [...(measureMoments || [])].sort(
            (a, b) => (Number(a.Position) || 0) - (Number(b.Position) || 0)
        )
        sorted.forEach((moment) => {
            const list = map.get(moment.ResearchID) || []
            list.push(moment)
            map.set(moment.ResearchID, list)
        })
        return map
    }, [measureMoments])

    // 'Nog niets gekoppeld' is een bewering: die mag pas als ALLEBEI de bronnen
    // leeg zijn — het eigen onderzoek én de vragenlijst van het programma.
    // AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
    const heeftProgrammaVragenlijst = hasProgrammeQuestionnaire(participations)

    if (researchLoaded && linkedResearch.length === 0 && !heeftProgrammaVragenlijst) {
        return (
            <p className='household-research-empty'>
                Er is nog geen onderzoek voor dit huishouden. Zet op het onderzoek de schakelaar
                <strong>'Geldt voor alle huishoudens'</strong> aan, of koppel het op de pagina van
                het huishouden, tabblad <strong>'Onderzoeken'</strong>.
            </p>
        )
    }

    return (
        <div className='household-research-links'>
            {/* De vragenlijst van het programma staat bovenaan: die is voor de
                keten leidend, en het eigen onderzoek is aanvullend. */}
            <ProgrammeResearchLink
                adressID={adressID}
                participations={participations}
                qrMode={effectiveQrMode}
            />
            {linkedResearch.map((research) => {
                const moments = momentsByResearch.get(research.ID) || []
                const measureMomentID = momentByResearch[research.ID] || ''
                const language = languageByResearch[research.ID] || 'nl'
                const links = buildQuestionnaireShareLinks({
                    client,
                    questionnaireID: research.QuestionnaireID,
                    measureMomentID,
                    researchID: research.ID,
                    respondentID: adressID,
                    language,
                })

                return (
                    <div key={research.ID} className='household-research-item'>
                        <h3 className='household-research-title'>{research.Title}</h3>

                        <label className='household-research-field'>
                            <span>Meetmoment</span>
                            <select
                                value={measureMomentID}
                                onChange={(e) => {
                                    // Zie de opmerking bij de taalkeuze hieronder: de waarde
                                    // moet buiten de updater vandaan komen.
                                    // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                    const value = e.target.value
                                    setMomentByResearch((prev) => ({ ...prev, [research.ID]: value }))
                                }}
                            >
                                <option value=''>-- Selecteer meetmoment --</option>
                                {moments.map((moment) => (
                                    <option key={moment.ID} value={moment.ID}>{moment.Title}</option>
                                ))}
                            </select>
                        </label>

                        <label className='household-research-field'>
                            <span>Taal</span>
                            <select
                                value={language}
                                onChange={(e) => {
                                    // Waarde EERST uitlezen. `e.target` is het levende
                                    // DOM-element; leest de updater hem pas als React al
                                    // draait, dan staat de oude waarde er alweer in en
                                    // schrijf je die terug.
                                    // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                    const value = e.target.value
                                    setLanguageByResearch((prev) => ({ ...prev, [research.ID]: value }))
                                }}
                            >
                                {languages.map((item) => (
                                    <option key={item.code} value={item.code}>
                                        {item.flag} {item.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {moments.length === 0 && (
                            <p className='household-research-empty'>
                                Dit onderzoek heeft nog geen meetmomenten.
                            </p>
                        )}

                        {links ? (
                            <div className='household-research-share'>
                                <div className='task-details-activityform-send-container'>
                                    <p><b>Link:</b></p>
                                    <CopyLink link={links.open} />
                                </div>
                                {effectiveQrMode === 'inline' ? (
                                    <div className='household-research-qrcode-inline'>
                                        <p><b>QR-code:</b></p>
                                        {/* Dezelfde waarde als de printbare pagina zou tonen —
                                            één bron, anders leidt de ene code naar een andere
                                            vragenlijst dan de andere.
                                            AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook */}
                                        <QRCode
                                            value={getQrcodeValue({
                                                host: window.location.hostname,
                                                client,
                                                questionnaire: research.QuestionnaireID,
                                                measureMomentID,
                                                researchID: research.ID,
                                                persona: adressID || 'undefined',
                                                route: 'research',
                                                languageCode: language,
                                            })}
                                            size={180}
                                        />
                                        <p className='household-research-qrcode-hint'>
                                            Laat de bewoner deze code scannen met zijn eigen telefoon.
                                        </p>
                                    </div>
                                ) : (
                                    <div className='task-details-activityform-send-container'>
                                        <p><b>QR-code:</b></p>
                                        <Tooltip content='Print de QR-code' top='-40px'>
                                            <img
                                                src={QrcodeIcon}
                                                className='household-research-qrcode-icon'
                                                alt=''
                                                onClick={() => history.push(links.qrcode)}
                                            />
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                        ) : (
                            moments.length > 0 && (
                                <p className='household-research-empty'>
                                    Kies een meetmoment om de link en QR-code te tonen.
                                </p>
                            )
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default HouseholdResearchLinks
